import React, { useState } from 'react';
import { Loader2, Calendar } from 'lucide-react';
import { expertDashboardApi, getErrorMessage } from '../../../lib/http';
import { ExpertProfile } from '../../../types';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';

interface ScheduleSettingsProps {
  profile: ExpertProfile;
  onUpdate: (profile: ExpertProfile) => void;
}

export const ScheduleSettings: React.FC<ScheduleSettingsProps> = ({
  profile,
  onUpdate,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const queryClient = useQueryClient();

  const handleToggleSeasonalSchedules = async () => {
    setIsSaving(true);
    try {
      const response = await expertDashboardApi.profile.update({
        use_seasonal_schedules: !profile.use_seasonal_schedules,
      });
      onUpdate(response.data);
      // Invalidate the profile query so WorkingHours page gets the update
      queryClient.invalidateQueries({ queryKey: ['expert-profile'] });
      toast.success(
        response.data.use_seasonal_schedules
          ? 'Seasonal schedules enabled'
          : 'Seasonal schedules disabled'
      );
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-purple-100 rounded-lg">
          <Calendar className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Schedule Settings</h3>
          <p className="text-sm text-gray-500">
            Configure your working hours preferences
          </p>
        </div>
      </div>

      {/* Seasonal Schedules Toggle */}
      <div className="flex items-center justify-between py-4 border-t border-gray-100">
        <div className="flex-1">
          <h4 className="text-sm font-medium text-gray-900">Seasonal Schedules</h4>
          <p className="text-sm text-gray-500 mt-1">
            Enable to create different schedules for specific date ranges (e.g., summer hours, holiday periods)
          </p>
        </div>
        <button
          onClick={handleToggleSeasonalSchedules}
          disabled={isSaving}
          className={`
            relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
            transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
            ${profile.use_seasonal_schedules ? 'bg-purple-600' : 'bg-gray-200'}
            ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <span className="sr-only">Toggle seasonal schedules</span>
          {isSaving ? (
            <span className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
            </span>
          ) : (
            <span
              className={`
                pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                transition duration-200 ease-in-out
                ${profile.use_seasonal_schedules ? 'translate-x-5' : 'translate-x-0'}
              `}
            />
          )}
        </button>
      </div>
    </div>
  );
};

export default ScheduleSettings;
